import { takeEvery, takeLatest, call, all, fork, put } from "@redux-saga/core/effects";
import {
   CREATE_NEW_FLOW_FROM_PORTAL,
   FETCH_WORKFLOW_ACTIVITIES,
   SET_WORKFLOW_ACTIVITIES,
   FETCH_ANALYTICS_ACTIVITIES,
   SET_ANALYTICS_ACTIVITIES,
   FETCH_PORTAL_FLOWS,
   SET_IS_LOADING_PORTAL_FLOWS,
   ARE_FLOWS_LOADED,
   SHARE_PORTAL_FLOW,
   FETCH_PORTAL_USER_FLOWS,
   SET_PORTAL_USER_FLOWS,
   SET_PORTAL_FLOWS
} from "redux/constants/portal";
import { createFlow, fetchFlows, fetchUserFlows } from "api/FlowDesignerApis";
import { fetchWorkflowActivities, fetchAnalyticsActivities, shareFlow } from "api/FlowsApi";
import { getCredentialById } from "api/CredentialManagementApi";
import { addPortalFlow, openSharerFlowDialog, setCredentialsInSharedFlow } from "redux/actions/portal/flowsActionCreator";
import { TRIGGER_TYPES } from "constants/flowDesigner";
import { createSchedule } from "api/FlowDesignerApis";
import { createResourceDependencies } from "redux/actions/portal/resourceDependenciesActionCreator";
import { getCopiedFlowDependencies, getResourceDependencyContextId } from "helpers/resourceDependencies";

/***** WORKERS *****/

function* getFlows(tenant_id) {
   try {
      const flows = yield call(fetchFlows, tenant_id);
      return flows || [];
   } catch (error) {
      console.error(error);
      return [];
   }
}

function* getUserFlows(tenant_id) {
   try {
      const userFlows = yield call(fetchUserFlows, tenant_id);
      return userFlows || [];
   } catch (error) {
      console.error(error);
      return [];
   }
}

function* handleCreateNewFlow({ payload, user, triggersAdded, cloneOptions }) {
   try {
      const {
         tenant_id,
         input: { logoUpload, ...input }
      } = payload;

      let flow = yield call(createFlow, tenant_id, input);

      if (flow) {
         input.tenant_id = tenant_id;
         flow = Object.assign(flow, input);
         yield put(addPortalFlow(flow));

         const originalFlowId = input.clone_flow?.original_flow_id;
         let dependencies = [];

         if (cloneOptions?.recreateDependencies && originalFlowId && flow?.id) {
            try {
               dependencies = yield call(getCopiedFlowDependencies, {
                  tenantId: tenant_id,
                  originalFlowId,
                  newFlowId: flow.id
               });
            } catch (e) {
               console.error("Failed to fetch dependencies for clone", e);
            }
         } else if (!cloneOptions?.isClone) {
            let integrations = [];
            if (input.integrations) {
               try {
                  integrations = typeof input.integrations === "string" ? JSON.parse(input.integrations) : input.integrations;
               } catch (e) {
                  null
               }
            }
            if (Array.isArray(integrations) && integrations.length > 0) {
               const contextId = getResourceDependencyContextId(flow.id, "integration");
               integrations.forEach((integrationId) => {
                  dependencies.push({
                     sourceId: flow.id,
                     targetId: integrationId,
                     sourceResourceType: "flow",
                     targetResourceType: "integration",
                     contextId
                  });
               });
            }

         }

         if (dependencies.length > 0 && flow?.id) {
            yield put(
               createResourceDependencies({
                  tenantId: tenant_id,
                  dependencies
               })
            );
         }

         if (window.location.href.includes("designer")) {
            window.open(`/designer/${flow.id}`, "_blank");

            return;
         }

         if (triggersAdded?.find((trigger) => trigger.type === TRIGGER_TYPES.SCHEDULE)) {
            const input = {
               created_by_id: user?.cxfUserId,
               description: "",
               end_date: null,
               name: `${flow?.name}-${Math.floor(1000 + Math.random() * 9000)}`,
               repeat_type: "not_repeat",
               start_date: new Date().toISOString(),
               status: "Paused",
               repetition: ""
            };

            yield call(createSchedule, tenant_id, input, true);
         }

         window.open(`/designer/${flow.id}`, "_blank", "noreferrer");
      }
   } catch (error) {
      console.error(error);
   }
}
function* handleFetchFlows({ payload }) {
   try {
      const { tenant_id } = payload;
      yield put({ type: SET_IS_LOADING_PORTAL_FLOWS, payload: true });
      const res = yield getFlows(tenant_id);
      yield put({ type: SET_PORTAL_FLOWS, payload: res });
      yield put({ type: ARE_FLOWS_LOADED });
      yield put({ type: SET_IS_LOADING_PORTAL_FLOWS, payload: false });
      yield;
   } catch (error) {
      console.error(error);
      yield put({ type: SET_IS_LOADING_PORTAL_FLOWS, payload: false });
   }
}

function* handleFetchWorkflowActivities({ payload }) {
   try {
      const { tenant_id, user_id } = payload;
      const periodMonths = 6; // last 6 months
      const startDate = new Date(new Date().setMonth(new Date().getMonth() - periodMonths)).toISOString();
      const endDate = new Date().toISOString();
      const res = yield call(fetchWorkflowActivities, tenant_id, user_id, startDate, endDate);
      yield put({ type: SET_WORKFLOW_ACTIVITIES, payload: res });
   } catch (error) {
      console.error(error);
   }
}
function* handleFetchAnalyticsActivities({ payload }) {
   try {
      const { tenant_id } = payload;

      const res = yield call(fetchAnalyticsActivities, tenant_id);
      yield put({ type: SET_ANALYTICS_ACTIVITIES, payload: res });
   } catch (error) {
      console.error(error);
   }
}

function* handleShareFlow({ payload }) {
   try {
      const { tenant_id, toShareFlows } = payload;
      const input = { user_flows: toShareFlows };
      const hasCredentials = yield checkCredentialsToShare(payload);

      const response = yield call(shareFlow, tenant_id, input);

      if (response?.isSuccessful && hasCredentials) {
         yield put(openSharerFlowDialog());
      }
   } catch (error) {
      console.error(error);
   }
}

export function* checkCredentialsToShare({ tenant_id, toShareFlows, flow: suppliedFlow }) {
   let hasCredentials = false;

   try {
      for (const item of toShareFlows) {
         const { flow_id } = item;
         const flow = suppliedFlow?.id === flow_id ? suppliedFlow : null;

         if (flow) {
            const { triggers, nodes } = JSON.parse(flow.steps);
            const credentialsInFLow = [];

            if (triggers) {
               for (const trigger of triggers) {
                  if (trigger.properties?.credentials && trigger.properties?.credentials?.required) {
                     const crName = trigger.properties?.credentials?.name;
                     const crType = trigger.properties?.credentials?.credentialType;
                     credentialsInFLow.push({ name: crName, type: crType });
                     hasCredentials = true;
                     break;
                  }
               }
            }

            if (nodes) {
               for (const node of nodes) {
                  if (node.data?.flowElement?.data && node.data.flowElement.data.credential) {
                     const credentialId = node.data.flowElement.data.credential.value;
                     if (!credentialId?.trim()) continue;

                     const res = yield call(getCredentialById, tenant_id, credentialId);

                     if (res?.resultObject) {
                        const crName = res?.resultObject.application?.name;
                        const crType = res?.resultObject.application?.applicationType;
                        credentialsInFLow.push({ name: crName, type: crType });
                        hasCredentials = true;
                        break;
                     }
                  }
               }
            }

            yield put(setCredentialsInSharedFlow(credentialsInFLow));
         }
      }
   } catch (error) {
      console.error(error);
   }

   return hasCredentials;
}

function* handleFetchUserFlows({ payload }) {
   try {
      const { tenant_id } = payload;
      const userFlows = yield getUserFlows(tenant_id);
      yield put({ type: SET_PORTAL_USER_FLOWS, payload: userFlows });
   } catch (error) {
      console.error(error);
   }
}

/***** WATCHERS *****/

function* watchCreateNewFlow() {
   yield takeEvery(CREATE_NEW_FLOW_FROM_PORTAL, handleCreateNewFlow);
}
function* watchFetchFlows() {
   yield takeLatest(FETCH_PORTAL_FLOWS, handleFetchFlows);
}
function* watchFetchWorkflowActivities() {
   yield takeEvery(FETCH_WORKFLOW_ACTIVITIES, handleFetchWorkflowActivities);
}
function* watchFetchAnalyticsActivities() {
   yield takeEvery(FETCH_ANALYTICS_ACTIVITIES, handleFetchAnalyticsActivities);
}
function* watchShareFlow() {
   yield takeEvery(SHARE_PORTAL_FLOW, handleShareFlow);
}
function* watchFetchUserFlows() {
   yield takeLatest(FETCH_PORTAL_USER_FLOWS, handleFetchUserFlows);
}

export function* rootFlowsSaga() {
   yield all([
      fork(watchCreateNewFlow),
      fork(watchFetchFlows),
      fork(watchFetchWorkflowActivities),
      fork(watchFetchAnalyticsActivities),
      fork(watchShareFlow),
      fork(watchFetchUserFlows)
   ]);
}
